"use client";

/**
 * HelpersBar (Phase 2 background helpers, D11) — the ask directly above the
 * composer: "N helpers are still working. Stop them too?", with Stop them and
 * Let them finish.
 *
 * D11: Stop stops the MAIN turn. Helpers keep working unless the person says
 * otherwise, and saying otherwise is one click. This surface mounts beside the
 * composer because that is where Stop lives (R80) — the quiet count line is
 * the STORY's task-level line (R81, spec section 5), so the number is never
 * shown twice.
 *
 * R82 — the set it speaks for is the LIVE one: a helper working, and a helper
 * waiting on the person. Only a lane that is over drops out. Self-hides the
 * moment the last live helper reports, so a task with none is untouched.
 */

import { isLiveHelperStatus } from "@/lib/teammates";
import { useLiveHelpers } from "@/stores/selectors";
import { useTaskStore } from "@/stores/taskStore";

export function HelpersBar({
  taskId,
  stopPrompt,
  onDismiss,
}: {
  /** The LEAD task whose helpers these are. */
  taskId: string | null | undefined;
  /** Stop was pressed on this task's turn — ask about its helpers too. */
  stopPrompt: boolean;
  /** Close the ask (answered either way, or the last helper finished). */
  onDismiss: () => void;
}) {
  const helpers = useLiveHelpers(taskId);
  const cancelTask = useTaskStore((s) => s.cancelTask);

  const live = helpers.filter((h) => isLiveHelperStatus(h.status));
  const n = live.length;
  if (!stopPrompt || n === 0) return null;
  const noun = n === 1 ? "helper" : "helpers";

  return (
    <div
      role="group"
      aria-label="Helpers still working"
      data-testid="helpers-bar"
      className="flex max-sm:flex-col flex-wrap items-center gap-2 px-3 pb-2 text-3 text-fg-muted"
    >
      <span>{`${n} ${noun} ${
        n === 1 ? "is" : "are"
      } still working. Stop them too?`}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            // Every live lane is tried, and the question closes only if at least one
            // interrupt actually went out (M4): with the socket down `cancelTask`
            // emits nothing, and a click that did nothing must not dismiss the ask.
            const sent = live
              .map((h) => cancelTask(h.teammateTaskId, "helper"))
              .some(Boolean);
            if (sent) onDismiss();
          }}
          className="h-7 px-2.5 rounded-8 border border-border-default bg-bg-muted text-3 font-medium text-fg-default transition-colors hover:bg-bg-subtle cursor-pointer"
        >
          Stop them
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="h-7 px-2.5 rounded-8 border border-transparent bg-transparent text-3 font-medium text-fg-muted transition-colors hover:text-fg-default cursor-pointer"
        >
          Let them finish
        </button>
      </div>
    </div>
  );
}
